import React from 'react';
import { useChangeContractorForJobsiteWorkersMutation } from 'apollo/generated/client-operations';
import { getCurrentISOFormattedDate, useIsMounted } from 'utils';
import { getGraphQLError } from 'utils/error';
import { WrenchIcon } from 'components/icons';
import { FormOnSubmit, ModalForm } from 'components/form';
import { AlertService } from 'components/alertService';
import {
  ChangeContractorFormData,
  ChangeContractorModalProps,
  ChangeType,
} from './types';
import {
  getDefaultValues,
  getFormInputsHook,
  useContractorsOptions,
} from './ChangeContractorModal.forms';

export function ChangeContractorModal(
  props: ChangeContractorModalProps,
): React.ReactElement {
  const {
    isOpen,
    closeModal,
    onClosed,
    jobsiteId,
    contractorId,
    jobsiteWorkers,
    refetchQueries,
  } = props;
  const isMounted = useIsMounted();
  const [isFetching, setIsFetching] = React.useState<boolean>(false);

  const [changeContractorForJobsiteWorkers] =
    useChangeContractorForJobsiteWorkersMutation({
      fetchPolicy: 'no-cache',
      refetchQueries,
    });

  const onSubmit: FormOnSubmit<ChangeContractorFormData> = async (
    data,
    event,
  ): Promise<void> => {
    if (isFetching) {
      return;
    }

    setIsFetching(true);

    const { changeType, newContractorId } = data;

    try {
      const result = await changeContractorForJobsiteWorkers({
        variables: {
          input: {
            jobsiteWorkerIds: jobsiteWorkers?.map(
              ({ jobsiteWorkerId }) => jobsiteWorkerId,
            ),
            newContractorId: newContractorId.value,
            startDate:
              changeType.value === ChangeType.Today
                ? getCurrentISOFormattedDate()
                : null,
          },
        },
      });

      const {
        changeContractorForJobsiteWorkersSuccess: successfulChanges,
        changeContractorForJobsiteWorkersError: errors,
      } = result.data?.changeContractorForJobsiteWorkers ?? {};

      if (successfulChanges?.length > 0) {
        AlertService.alert(
          'success',
          `Contractor successfully changed${
            successfulChanges.length > 1
              ? ` for ${successfulChanges.length} workers`
              : ''
          }`,
          '',
        );
      }

      // This handles the case when MULTIPLE workers are being reassigned
      // and some of them fail.
      // When a single worker is being reassigned and it fails,
      // the error is thrown from the resolver so that
      // the modal won't close due to `refetchQueries`.
      if (errors?.length > 0) {
        AlertService.alert(
          'danger',
          `Failed to change contractor${
            errors.length > 1 ? ` for ${errors.length} workers` : ''
          }`,
          errors.map(({ errorMessage }) => errorMessage).join('\n'),
        );
      }

      closeModal();
    } catch (error) {
      event.preventDefault();
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    } finally {
      if (isMounted()) {
        setIsFetching(false);
      }
    }
  };

  const contractorOptions = useContractorsOptions({
    jobsiteId,
    contractorId,
    skip: !isOpen,
  });
  const formInputs = getFormInputsHook({ contractorOptions });
  const defaultValues = React.useMemo(
    () => getDefaultValues({ contractorOptions }),
    [contractorOptions],
  );

  return (
    <ModalForm
      open={isOpen}
      title="Change Contractor"
      setOpen={closeModal}
      afterLeave={onClosed}
      inputs={formInputs}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      inputsContainerClassName="odin-grid odin-grid-cols-6 odin-gap-6"
      actionText="Change Contractor"
      actionIcon={WrenchIcon}
      actionButtonWithSpinner={isFetching}
    />
  );
}
